import React, { useMemo } from 'react';
import * as THREE from 'three';

const CELL = 0.15; // 150mm in meters

// Helper: rounded box geometry
function RoundedBox({ width, height, depth, radius = 0.005, color, ...props }) {
  return (
    <mesh {...props} castShadow receiveShadow>
      <boxGeometry args={[width, height, depth]} />
      <meshStandardMaterial color={color} roughness={0.6} metalness={0.1} />
    </mesh>
  );
}

// ─── SEATING ───────────────────────────────────────────────────────

export function SeatModel({ width, height, color = '#8b5cf6', fabricColor }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  const seatColor = fabricColor || '#e8d5b7';
  return (
    <group>
      {/* Seat base */}
      <RoundedBox width={w} height={0.06} depth={h * 0.6} color="#2a2a2a" position={[0, 0.05, h * 0.1]} />
      {/* Cushion */}
      <mesh position={[0, 0.09, h * 0.1]} castShadow>
        <boxGeometry args={[w * 0.9, 0.04, h * 0.55]} />
        <meshStandardMaterial color={seatColor} roughness={0.8} />
      </mesh>
      {/* Backrest */}
      <mesh position={[0, 0.2, -h * 0.2]} castShadow>
        <boxGeometry args={[w * 0.85, 0.22, 0.04]} />
        <meshStandardMaterial color={seatColor} roughness={0.8} />
      </mesh>
      {/* Headrest */}
      <mesh position={[0, 0.34, -h * 0.2]} castShadow>
        <boxGeometry args={[w * 0.5, 0.08, 0.05]} />
        <meshStandardMaterial color={seatColor} roughness={0.7} />
      </mesh>
      {/* Armrests */}
      <mesh position={[w * 0.45, 0.12, 0]} castShadow>
        <boxGeometry args={[0.02, 0.06, h * 0.5]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.5} />
      </mesh>
      <mesh position={[-w * 0.45, 0.12, 0]} castShadow>
        <boxGeometry args={[0.02, 0.06, h * 0.5]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.5} />
      </mesh>
    </group>
  );
}

export function ReclinerModel({ width, height, color = '#8b5cf6', fabricColor }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  const seatColor = fabricColor || '#f5f0e8';
  return (
    <group>
      {/* Base frame */}
      <RoundedBox width={w} height={0.04} depth={h} color="#1a1a2e" position={[0, 0.02, 0]} />
      {/* Seat cushion - thick & plush */}
      <mesh position={[0, 0.08, h * 0.15]} castShadow>
        <boxGeometry args={[w * 0.9, 0.06, h * 0.5]} />
        <meshStandardMaterial color={seatColor} roughness={0.85} />
      </mesh>
      {/* Reclined backrest */}
      <mesh position={[0, 0.18, -h * 0.25]} rotation={[0.15, 0, 0]} castShadow>
        <boxGeometry args={[w * 0.85, 0.28, 0.05]} />
        <meshStandardMaterial color={seatColor} roughness={0.85} />
      </mesh>
      {/* Headrest pillow */}
      <mesh position={[0, 0.36, -h * 0.28]} castShadow>
        <boxGeometry args={[w * 0.45, 0.1, 0.06]} />
        <meshStandardMaterial color={seatColor} roughness={0.9} />
      </mesh>
      {/* Leg rest (extended) */}
      <mesh position={[0, 0.06, h * 0.4]} rotation={[-0.2, 0, 0]} castShadow>
        <boxGeometry args={[w * 0.8, 0.03, h * 0.25]} />
        <meshStandardMaterial color={seatColor} roughness={0.85} />
      </mesh>
      {/* Side panels */}
      {[-1, 1].map(s => (
        <mesh key={s} position={[s * w * 0.47, 0.1, 0]} castShadow>
          <boxGeometry args={[0.025, 0.14, h * 0.7]} />
          <meshStandardMaterial color="#222" roughness={0.4} metalness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

export function SofaBedModel({ width, height, color, fabricColor }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  const seatColor = fabricColor || '#94a3b8';
  return (
    <group>
      <RoundedBox width={w} height={0.04} depth={h} color="#1a1a2e" position={[0, 0.02, 0]} />
      <mesh position={[0, 0.08, 0]} castShadow>
        <boxGeometry args={[w * 0.95, 0.06, h * 0.95]} />
        <meshStandardMaterial color={seatColor} roughness={0.9} />
      </mesh>
      {/* Back cushions */}
      <mesh position={[0, 0.17, -h * 0.4]} castShadow>
        <boxGeometry args={[w * 0.9, 0.14, 0.06]} />
        <meshStandardMaterial color={seatColor} roughness={0.85} />
      </mesh>
      {/* Pillows */}
      {[-0.25, 0.25].map((xo, i) => (
        <mesh key={i} position={[xo * w, 0.14, -h * 0.35]} castShadow>
          <boxGeometry args={[w * 0.3, 0.06, 0.06]} />
          <meshStandardMaterial color="#e2e8f0" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

export function FoldableSeatModel({ width, height, color, fabricColor }) {
  const w = width * CELL * 0.8;
  const h = height * CELL * 0.8;
  return (
    <group>
      <RoundedBox width={w} height={0.03} depth={h * 0.5} color="#555" position={[0, 0.06, 0]} />
      <mesh position={[0, 0.12, -h * 0.2]} castShadow>
        <boxGeometry args={[w * 0.85, 0.15, 0.02]} />
        <meshStandardMaterial color={fabricColor || '#666'} roughness={0.7} />
      </mesh>
      {/* Legs */}
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx,sz], i) => (
        <mesh key={i} position={[sx * w * 0.35, 0.03, sz * h * 0.15]}>
          <cylinderGeometry args={[0.005, 0.005, 0.06, 8]} />
          <meshStandardMaterial color="#999" metalness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

// ─── UTILITY ───────────────────────────────────────────────────────

export function PantryModel({ width, height, color, woodFinish }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  const woodColor = woodFinish || '#5c3d2e';
  return (
    <group>
      {/* Counter */}
      <RoundedBox width={w} height={0.03} depth={h} color="#e2e8f0" position={[0, 0.18, 0]} />
      {/* Cabinet body */}
      <RoundedBox width={w} height={0.16} depth={h} color={woodColor} position={[0, 0.09, 0]} />
      {/* Doors */}
      {[-0.25, 0.25].map((xo, i) => (
        <mesh key={i} position={[xo * w, 0.09, h * 0.501]} castShadow>
          <boxGeometry args={[w * 0.45, 0.13, 0.005]} />
          <meshStandardMaterial color={woodColor} roughness={0.5} />
        </mesh>
      ))}
      {/* Sink */}
      <mesh position={[-w * 0.25, 0.19, -h * 0.2]} castShadow>
        <cylinderGeometry args={[0.04, 0.035, 0.02, 16]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Handles */}
      {[-0.25, 0.25].map((xo, i) => (
        <mesh key={`h${i}`} position={[xo * w, 0.09, h * 0.51]}>
          <boxGeometry args={[0.02, 0.005, 0.01]} />
          <meshStandardMaterial color="#aaa" metalness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function FridgeModel({ width, height, color }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      <RoundedBox width={w} height={0.25} depth={h} color="#e0e0e0" position={[0, 0.125, 0]} />
      <mesh position={[0, 0.125, h * 0.501]} castShadow>
        <boxGeometry args={[w * 0.9, 0.2, 0.005]} />
        <meshStandardMaterial color="#d0d0d0" roughness={0.3} metalness={0.4} />
      </mesh>
      <mesh position={[w * 0.3, 0.15, h * 0.51]}>
        <boxGeometry args={[0.005, 0.1, 0.01]} />
        <meshStandardMaterial color="#888" metalness={0.8} />
      </mesh>
    </group>
  );
}

export function TrayTableModel({ width, height }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      <RoundedBox width={w} height={0.015} depth={h} color="#d4a574" position={[0, 0.12, 0]} />
      <mesh position={[-w * 0.35, 0.06, 0]}>
        <cylinderGeometry args={[0.008, 0.008, 0.12, 8]} />
        <meshStandardMaterial color="#888" metalness={0.6} />
      </mesh>
      <mesh position={[w * 0.35, 0.06, 0]}>
        <cylinderGeometry args={[0.008, 0.008, 0.12, 8]} />
        <meshStandardMaterial color="#888" metalness={0.6} />
      </mesh>
    </group>
  );
}

export function BBQModel({ width, height }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      <RoundedBox width={w} height={0.08} depth={h} color="#333" position={[0, 0.08, 0]} />
      {/* Grill grate */}
      <mesh position={[0, 0.13, 0]} castShadow>
        <boxGeometry args={[w * 0.8, 0.005, h * 0.7]} />
        <meshStandardMaterial color="#666" metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Grill lines */}
      {Array.from({ length: 5 }).map((_, i) => (
        <mesh key={i} position={[-w * 0.3 + i * w * 0.15, 0.135, 0]}>
          <boxGeometry args={[0.003, 0.003, h * 0.65]} />
          <meshStandardMaterial color="#555" metalness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function ToiletModel({ width, height }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      {/* Room walls */}
      <mesh position={[0, 0.15, -h * 0.49]} castShadow>
        <boxGeometry args={[w, 0.3, 0.01]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.8} />
      </mesh>
      <mesh position={[-w * 0.49, 0.15, 0]} castShadow>
        <boxGeometry args={[0.01, 0.3, h]} />
        <meshStandardMaterial color="#ddd" roughness={0.8} />
      </mesh>
      {/* Toilet bowl */}
      <mesh position={[w * 0.15, 0.06, h * 0.15]} castShadow>
        <cylinderGeometry args={[0.05, 0.04, 0.12, 16]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.3} />
      </mesh>
      {/* Toilet tank */}
      <mesh position={[w * 0.15, 0.12, -h * 0.1]}>
        <boxGeometry args={[0.08, 0.1, 0.05]} />
        <meshStandardMaterial color="#f5f5f5" roughness={0.3} />
      </mesh>
      {/* Wash basin */}
      <mesh position={[-w * 0.2, 0.14, -h * 0.35]}>
        <cylinderGeometry args={[0.04, 0.03, 0.03, 16]} />
        <meshStandardMaterial color="#f0f0f0" metalness={0.1} roughness={0.3} />
      </mesh>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial color="#d4d4d8" roughness={0.6} />
      </mesh>
    </group>
  );
}

// ─── LIFESTYLE ─────────────────────────────────────────────────────

export function TentModel({ width, height }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  return (
    <group>
      <mesh position={[0, 0.12, 0]} castShadow>
        <coneGeometry args={[w * 0.5, 0.25, 4]} />
        <meshStandardMaterial color="#4a7c59" roughness={0.9} side={THREE.DoubleSide} />
      </mesh>
      {/* Poles */}
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx,sz], i) => (
        <mesh key={i} position={[sx * w * 0.3, 0.06, sz * h * 0.3]}>
          <cylinderGeometry args={[0.003, 0.003, 0.12, 6]} />
          <meshStandardMaterial color="#8B7355" />
        </mesh>
      ))}
    </group>
  );
}

export function CanopyModel({ width, height }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  return (
    <group>
      <mesh position={[0, 0.2, 0]} castShadow>
        <boxGeometry args={[w, 0.005, h]} />
        <meshStandardMaterial color="#718096" roughness={0.7} side={THREE.DoubleSide} />
      </mesh>
      {[[-1,-1],[1,-1]].map(([sx,sz], i) => (
        <mesh key={i} position={[sx * w * 0.4, 0.1, sz * h * 0.4]}>
          <cylinderGeometry args={[0.005, 0.005, 0.2, 8]} />
          <meshStandardMaterial color="#999" metalness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

export function UmbrellaModel() {
  return (
    <group>
      <mesh position={[0, 0.04, 0]}>
        <cylinderGeometry args={[0.02, 0.025, 0.08, 8]} />
        <meshStandardMaterial color="#444" />
      </mesh>
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.003, 0.003, 0.04, 6]} />
        <meshStandardMaterial color="#888" metalness={0.6} />
      </mesh>
    </group>
  );
}

export function OutdoorSeatModel({ width, height, fabricColor }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      {[[-0.25, 0], [0.25, 0]].map(([xo, zo], i) => (
        <group key={i} position={[xo * w, 0, zo * h]}>
          <RoundedBox width={w * 0.35} height={0.03} depth={h * 0.4} color={fabricColor || '#5a7d5a'} position={[0, 0.06, 0]} />
          {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx,sz], j) => (
            <mesh key={j} position={[sx * w * 0.12, 0.03, sz * h * 0.12]}>
              <cylinderGeometry args={[0.004, 0.004, 0.06, 6]} />
              <meshStandardMaterial color="#8B7355" />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

// ─── STORAGE ───────────────────────────────────────────────────────

export function CabinetModel({ width, height, woodFinish }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  const woodColor = woodFinish || '#8B7355';
  return (
    <group>
      <RoundedBox width={w} height={0.12} depth={h} color={woodColor} position={[0, 0.2, 0]} />
      {/* Door line */}
      <mesh position={[0, 0.2, h * 0.501]}>
        <boxGeometry args={[0.002, 0.1, 0.002]} />
        <meshStandardMaterial color="#666" />
      </mesh>
      {/* LED strip */}
      <mesh position={[0, 0.138, h * 0.45]}>
        <boxGeometry args={[w * 0.8, 0.003, 0.003]} />
        <meshStandardMaterial color="#60a5fa" emissive="#3b82f6" emissiveIntensity={2} />
      </mesh>
    </group>
  );
}

export function StorageModel({ width, height }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <group>
      <RoundedBox width={w} height={0.08} depth={h} color="#4a4a5a" position={[0, 0.04, 0]} />
      <mesh position={[0, 0.085, 0]}>
        <boxGeometry args={[w * 0.95, 0.005, h * 0.95]} />
        <meshStandardMaterial color="#555" roughness={0.4} metalness={0.3} />
      </mesh>
    </group>
  );
}

export function WardrobeModel({ width, height, woodFinish }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  const woodColor = woodFinish || '#5c3d2e';
  return (
    <group>
      <RoundedBox width={w} height={0.35} depth={h} color={woodColor} position={[0, 0.175, 0]} />
      {/* Door split */}
      <mesh position={[0, 0.175, h * 0.501]}>
        <boxGeometry args={[0.002, 0.3, 0.002]} />
        <meshStandardMaterial color="#333" />
      </mesh>
      {/* Handles */}
      {[-0.15, 0.15].map((xo, i) => (
        <mesh key={i} position={[xo * w / 0.3, 0.18, h * 0.51]}>
          <boxGeometry args={[0.005, 0.03, 0.008]} />
          <meshStandardMaterial color="#aaa" metalness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function PartitionModel({ width, height }) {
  const w = width * CELL * 0.95;
  const h = height * CELL * 0.95;
  return (
    <group>
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[w, 0.3, 0.015]} />
        <meshStandardMaterial color="#94a3b8" transparent opacity={0.6} roughness={0.2} />
      </mesh>
      {/* Frame */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[w, 0.008, 0.02]} />
        <meshStandardMaterial color="#666" metalness={0.5} />
      </mesh>
    </group>
  );
}

// ─── ELECTRONICS ───────────────────────────────────────────────────

export function SpeakerModel({ width, height }) {
  const w = width * CELL * 0.8;
  const h = height * CELL * 0.8;
  return (
    <group>
      <RoundedBox width={w} height={0.1} depth={h} color="#1a1a1a" position={[0, 0.05, 0]} />
      <mesh position={[-w * 0.2, 0.06, h * 0.3]}>
        <cylinderGeometry args={[0.02, 0.02, 0.01, 16]} />
        <meshStandardMaterial color="#333" metalness={0.3} />
      </mesh>
      <mesh position={[w * 0.15, 0.06, h * 0.3]}>
        <cylinderGeometry args={[0.03, 0.03, 0.01, 16]} />
        <meshStandardMaterial color="#333" metalness={0.3} />
      </mesh>
      {/* Mesh grille */}
      <mesh position={[0, 0.101, 0]}>
        <boxGeometry args={[w * 0.9, 0.002, h * 0.8]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.8} />
      </mesh>
    </group>
  );
}

export function TVModel({ width, height }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.8;
  return (
    <group>
      {/* Screen */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <boxGeometry args={[w, 0.18, 0.012]} />
        <meshStandardMaterial color="#111" roughness={0.1} metalness={0.5} />
      </mesh>
      {/* Screen face */}
      <mesh position={[0, 0.2, 0.007]}>
        <planeGeometry args={[w * 0.92, 0.15]} />
        <meshStandardMaterial color="#0a0a2e" emissive="#1a1a4e" emissiveIntensity={0.3} />
      </mesh>
      {/* Stand */}
      <mesh position={[0, 0.06, 0]}>
        <cylinderGeometry args={[0.01, 0.02, 0.12, 8]} />
        <meshStandardMaterial color="#444" metalness={0.6} />
      </mesh>
    </group>
  );
}

export function ControlPanelModel() {
  return (
    <group>
      <RoundedBox width={0.1} height={0.08} depth={0.1} color="#1e293b" position={[0, 0.04, 0]} />
      {/* Screen */}
      <mesh position={[0, 0.06, 0.04]}>
        <planeGeometry args={[0.07, 0.04]} />
        <meshStandardMaterial color="#0f172a" emissive="#3b82f6" emissiveIntensity={0.5} />
      </mesh>
      {/* Buttons */}
      {[-0.02, 0, 0.02].map((xo, i) => (
        <mesh key={i} position={[xo, 0.082, 0]}>
          <cylinderGeometry args={[0.005, 0.005, 0.003, 8]} />
          <meshStandardMaterial color="#60a5fa" emissive="#3b82f6" emissiveIntensity={1} />
        </mesh>
      ))}
    </group>
  );
}

export function ChargingDockModel() {
  return (
    <group>
      <RoundedBox width={0.08} height={0.06} depth={0.08} color="#2a2a3a" position={[0, 0.03, 0]} />
      {/* USB ports */}
      {[-0.015, 0.015].map((xo, i) => (
        <mesh key={i} position={[xo, 0.04, 0.041]}>
          <boxGeometry args={[0.008, 0.003, 0.003]} />
          <meshStandardMaterial color="#666" metalness={0.5} />
        </mesh>
      ))}
      {/* LED indicator */}
      <mesh position={[0, 0.062, 0]}>
        <sphereGeometry args={[0.003, 8, 8]} />
        <meshStandardMaterial color="#10b981" emissive="#10b981" emissiveIntensity={3} />
      </mesh>
    </group>
  );
}

// ─── SPECIAL: Platform Lift (Election Campaign) ─────────────────

export function PlatformLiftModel({ width, height }) {
  const w = width * CELL * 0.9;
  const h = height * CELL * 0.9;
  return (
    <group>
      {/* Platform base */}
      <RoundedBox width={w} height={0.02} depth={h} color="#e0c080" position={[0, 0.25, 0]} />
      {/* Railing */}
      {[[-1, 0], [1, 0], [0, -1], [0, 1]].map(([sx, sz], i) => {
        const isX = sx !== 0;
        return (
          <mesh key={i} position={[sx * w * 0.48, 0.35, sz * h * 0.48]}>
            <boxGeometry args={[isX ? 0.01 : w * 0.9, 0.005, isX ? h * 0.9 : 0.01]} />
            <meshStandardMaterial color="#c0a060" metalness={0.5} />
          </mesh>
        );
      })}
      {/* Hydraulic lift columns */}
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx,sz], i) => (
        <mesh key={i} position={[sx * w * 0.4, 0.125, sz * h * 0.4]}>
          <cylinderGeometry args={[0.012, 0.015, 0.25, 8]} />
          <meshStandardMaterial color="#888" metalness={0.7} roughness={0.3} />
        </mesh>
      ))}
      {/* Railings vertical */}
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx,sz], i) => (
        <mesh key={`r${i}`} position={[sx * w * 0.48, 0.32, sz * h * 0.48]}>
          <cylinderGeometry args={[0.005, 0.005, 0.14, 6]} />
          <meshStandardMaterial color="#c0a060" metalness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

// ─── FALLBACK ──────────────────────────────────────────────────────

export function FallbackModel({ width, height, color }) {
  const w = width * CELL * 0.85;
  const h = height * CELL * 0.85;
  return (
    <RoundedBox width={w} height={0.1} depth={h} color={color || '#666'} position={[0, 0.05, 0]} />
  );
}
